import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowLeft,
  ExternalLink,
  FileText,
  LoaderCircle,
  LockKeyhole,
  Plus,
  ShieldCheck,
  Stethoscope,
} from "lucide-react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError, type ClinicalAttachment, type CrmAccess } from "./api";
import { useAuth } from "./auth-context";
import { useCrm } from "./crm-context";
import { ClinicalActComposer } from "./clinical-act";
import { ClinicalDocuments } from "./clinical-followup";
import { MedicationPanel } from "./medication-panel";
import { date, HealthFields, HealthSection, VALUE_LABELS } from "./health-record-fields";
import {
  clearTemporaryVeterinarySession,
  readTemporaryVeterinarySession,
  saveTemporaryVeterinarySession,
  veterinaryAccessApi,
  type TemporaryVeterinaryContext,
  type TemporaryVeterinarySession,
} from "./veterinary-access-api";

const APP_URL = (import.meta.env.VITE_APP_URL as string | undefined) ?? "https://app.tappet.com.ar";

function errorText(reason: unknown): string {
  return reason instanceof Error
    ? reason.message
    : "No pudimos completar esta acción. Intentá nuevamente.";
}

export function VeterinaryAccessScreen(): ReactNode {
  const auth = useAuth();
  const crm = useCrm();
  const location = useLocation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const petPublicSlug = (search.get("pet") ?? "").trim();
  const [organizationId, setOrganizationId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [session, setSession] = useState<TemporaryVeterinarySession | null>(null);
  const [data, setData] = useState<TemporaryVeterinaryContext | null>(null);
  const [documents, setDocuments] = useState<ClinicalAttachment[]>([]);
  const [link, setLink] = useState<CrmAccess | null>(null);
  const [composer, setComposer] = useState(false);
  const [paging, setPaging] = useState(false);
  const organizations = useMemo(
    () =>
      crm.context?.organizations.filter((organization) =>
        organization.availableWorkspaces.some(
          (workspace) =>
            workspace.type === "VETERINARY" && workspace.capabilities.includes("CLINICAL_READ"),
        ),
      ) ?? [],
    [crm.context],
  );

  useEffect(() => {
    if (!auth.user || !organizations.length) return;
    const stored = readTemporaryVeterinarySession(auth.user.id, petPublicSlug);
    const next = organizations.some((organization) => organization.id === stored?.organizationId)
      ? stored!.organizationId
      : organizations.some((organization) => organization.id === crm.activeOrganization?.id)
        ? crm.activeOrganization!.id
        : organizations.length === 1
          ? organizations[0]!.id
          : "";
    setOrganizationId((current) =>
      organizations.some((organization) => organization.id === current) ? current : next,
    );
  }, [auth.user?.id, organizations, crm.activeOrganization?.id, petPublicSlug]);

  useEffect(() => {
    if (!organizationId || !auth.user || !petPublicSlug || auth.status !== "authenticated") return;
    let alive = true;
    setChecking(true);
    setError(null);
    setSession(null);
    setData(null);
    setDocuments([]);
    setLink(null);
    setComposer(false);
    const userId = auth.user.id;
    void (async () => {
      try {
        const patients = await api.patients(organizationId, petPublicSlug);
        if (!alive) return;
        const linked = patients.find(
          (patient) => patient.publicSlug === petPublicSlug && patient.clinicalDetailsAvailable,
        );
        if (linked) {
          await crm.selectWorkspace(organizationId, "VETERINARY");
          if (alive)
            void navigate(`/pacientes/${encodeURIComponent(linked.id)}`, { replace: true });
          return;
        }
        const stored = readTemporaryVeterinarySession(userId, petPublicSlug);
        if (stored?.organizationId === organizationId) {
          const [context, assets] = await Promise.all([
            veterinaryAccessApi.context(stored),
            veterinaryAccessApi.documents(stored),
          ]);
          if (alive) {
            setSession(stored);
            setData(context);
            setDocuments(assets);
          }
        }
      } catch (reason) {
        if (alive) {
          if (reason instanceof ApiError && [401, 403].includes(reason.status))
            clearTemporaryVeterinarySession();
          setError(errorText(reason));
        }
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => {
      alive = false;
    };
    // selectWorkspace changes identity as its context updates, but must not reset an active medical form.
  }, [organizationId, auth.user?.id, auth.status, petPublicSlug]);

  function endTemporaryAccess(message?: string): void {
    clearTemporaryVeterinarySession();
    setSession(null);
    setData(null);
    setDocuments([]);
    setComposer(false);
    if (message) setNotice(message);
  }

  useEffect(() => {
    if (!session) return;
    const clear = () =>
      endTemporaryAccess(
        "El acceso temporal terminó. Pedí un nuevo código al tutor si necesitás continuar.",
      );
    const timer = window.setTimeout(clear, Math.max(0, Date.parse(session.expiresAt) - Date.now()));
    window.addEventListener("tappet-temporary-vet-access-expired", clear);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("tappet-temporary-vet-access-expired", clear);
    };
  }, [session]);

  useEffect(() => {
    if (auth.status === "anonymous") endTemporaryAccess();
  }, [auth.status]);

  async function redeem(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!auth.user || !organizationId || !petPublicSlug || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await veterinaryAccessApi.redeem(organizationId, code.trim(), petPublicSlug);
      const granted = {
        token: result.sessionToken,
        expiresAt: result.expiresAt,
        organizationId,
        userId: auth.user.id,
        petPublicSlug,
      };
      saveTemporaryVeterinarySession(granted);
      setCode("");
      setSession(granted);
      setData(result);
      setDocuments(await veterinaryAccessApi.documents(granted));
    } catch (reason) {
      if (reason instanceof ApiError && [401, 403].includes(reason.status)) endTemporaryAccess();
      setError(errorText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function requestLink(): Promise<void> {
    if (busy || !organizationId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.requestAccess(organizationId, {
        identifier: petPublicSlug,
        scope: "CLINICAL",
      });
      setLink(result);
      setNotice(
        "Solicitud enviada. El tutor principal debe aprobarla; el código temporal no crea un vínculo permanente.",
      );
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === "CRM_PATIENT_ALREADY_LINKED" && data) {
        await crm.selectWorkspace(organizationId, "VETERINARY");
        void navigate(`/pacientes/${encodeURIComponent(data.pet.id)}`);
      } else setError(errorText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function loadMore(kind: "consultations" | "vaccinations"): Promise<void> {
    if (!session || !data || paging) return;
    setPaging(true);
    setError(null);
    try {
      const page = data.pagination[kind];
      const next = await veterinaryAccessApi.context(session, {
        [`${kind}Offset`]: page.offset + page.limit,
      });
      setData((current) =>
        current
          ? {
              ...current,
              [kind]: [
                ...current[kind],
                ...next[kind].filter(
                  (item) => !current[kind].some((existing) => existing.id === item.id),
                ),
              ],
              pagination: { ...current.pagination, [kind]: next.pagination[kind] },
            }
          : null,
      );
    } catch (reason) {
      setError(errorText(reason));
    } finally {
      setPaging(false);
    }
  }

  if (auth.status === "loading" || crm.status === "loading")
    return (
      <main className="standalone-state" role="status">
        <LoaderCircle className="spin" /> Preparando acceso veterinario…
      </main>
    );
  if (auth.status === "anonymous")
    return (
      <Navigate to="/ingresar" replace state={{ from: `${location.pathname}${location.search}` }} />
    );

  return (
    <main className="vet-access-page">
      <header className="vet-access-heading">
        <a
          className="back-link"
          href={
            petPublicSlug
              ? `${APP_URL}/p/${encodeURIComponent(petPublicSlug)}?source=tag&view=public`
              : `${APP_URL}/`
          }
        >
          <ArrowLeft size={17} /> Volver al perfil de la mascota
        </a>
        <span className="eyebrow">
          <Stethoscope size={17} /> Tappet Vet
        </span>
        <h1>{data?.pet.name ?? "Acceso veterinario"}</h1>
        <p>
          {data
            ? "Historia de salud autorizada por el tutor. Los registros clínicos conservan su autoría."
            : "Abrí la ficha autorizada de esta mascota desde tu espacio veterinario."}
        </p>
      </header>
      {error && (
        <div className="error-panel" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="success-panel" role="status">
          {notice}
        </div>
      )}
      {crm.status === "error" ? (
        <section className="panel">
          <p>{crm.error}</p>
          <button className="button button-secondary" onClick={() => void crm.reload()}>
            Reintentar
          </button>
        </section>
      ) : !petPublicSlug ? (
        <section className="panel">
          <p>Falta identificar la mascota. Abrí este acceso desde su perfil público.</p>
          <Link to="/pacientes">Ir a pacientes</Link>
        </section>
      ) : !organizations.length ? (
        <section className="panel">
          <h2>Se requiere acceso clínico veterinario</h2>
          <p>
            Esta cuenta no tiene un espacio veterinario habilitado para consultar historias
            clínicas. Los permisos de otros rubros no habilitan datos de salud.
          </p>
          <Link to="/">Volver a Tappet Pro</Link>
        </section>
      ) : (
        <>
          <section className="panel vet-access-organization">
            <label>
              Veterinaria
              <select
                value={organizationId}
                disabled={busy || Boolean(session)}
                onChange={(event) => {
                  setOrganizationId(event.target.value);
                  setCode("");
                  setNotice(null);
                }}
              >
                <option value="">Elegí tu veterinaria</option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </label>
            <span>
              <ShieldCheck size={18} /> {auth.user?.displayName}
            </span>
          </section>
          {checking ? (
            <p className="panel" role="status">
              Comprobando vínculo y permisos…
            </p>
          ) : !session || !data ? (
            <section className="panel vet-access-entry">
              <h2>Autorización del tutor</h2>
              <p>
                Si todavía no existe un vínculo aprobado, pedile al tutor el código temporal que
                genera en Salud. No se revela la historia sólo por escanear el tag.
              </p>
              <form onSubmit={(event) => void redeem(event)}>
                <label>
                  Código temporal del tutor
                  <input
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    autoCapitalize="characters"
                    autoComplete="one-time-code"
                    maxLength={40}
                    required
                    minLength={4}
                    disabled={!organizationId || busy}
                  />
                </label>
                <button
                  className="button button-primary"
                  disabled={!organizationId || busy || code.trim().length < 4}
                >
                  <LockKeyhole size={17} /> {busy ? "Validando…" : "Abrir historia clínica"}
                </button>
              </form>
              <p className="muted">
                Para registrar atenciones desde el CRM necesitás una matrícula veterinaria aprobada.
                Cada lectura y carga queda auditada.
              </p>
            </section>
          ) : (
            <>
              <section className="consent-note vet-access-notice">
                <LockKeyhole size={20} />
                <div>
                  <strong>Acceso temporal hasta {date(session.expiresAt)}</strong>
                  <p>
                    Este permiso no convierte a la mascota en paciente permanente. El tutor puede
                    revocarlo en cualquier momento.
                  </p>
                </div>
              </section>
              <section className="panel vet-access-actions">
                <button
                  className="button button-primary"
                  disabled={composer || !data.professional.verified}
                  onClick={() => setComposer(true)}
                >
                  <Plus size={17} /> Nueva atención
                </button>
                <button
                  className="button button-secondary"
                  disabled={busy || link?.status === "PENDING"}
                  onClick={() => void requestLink()}
                >
                  {link?.status === "PENDING"
                    ? "Vínculo pendiente de aprobación"
                    : "Agregar como paciente"}
                </button>
                <button
                  className="button-link"
                  onClick={() => endTemporaryAccess("Se cerró este acceso en el dispositivo.")}
                >
                  Cerrar acceso temporal
                </button>
              </section>
              {composer && (
                <ClinicalActComposer
                  patientName={data.pet.name}
                  signer={{
                    fullName: data.professional.fullName,
                    licenseNumber: data.professional.licenseNumber ?? "",
                    jurisdiction: data.professional.jurisdiction ?? "",
                  }}
                  onCancel={() => setComposer(false)}
                  onSigned={async (input, key) => {
                    await veterinaryAccessApi.consult(session, input, key);
                    const context = await veterinaryAccessApi.context(session);
                    setData(context);
                    setComposer(false);
                    setNotice(
                      "Atención firmada. Podés adjuntar estudios y documentos en ese registro.",
                    );
                  }}
                />
              )}
              <div className="vet-access-health-grid">
                <section className="panel vet-access-section">
                  <h2>Ficha y resumen de salud</h2>
                  <HealthFields record={data.health.patient} />
                  <HealthFields record={data.summary} />
                </section>
                <section className="panel vet-access-section">
                  <h2>Salud reproductiva</h2>
                  <HealthFields record={data.health.reproductive} />
                </section>
                <MedicationPanel patientName={data.pet.name} medications={data.health.medications} consultations={data.consultations} canWrite
                  onCreated={async (input, key) => {
                    const created = await veterinaryAccessApi.createMedication(session, input, key);
                    setData(await veterinaryAccessApi.context(session));
                    return created;
                  }}
                  onStopped={async (id, input, key) => {
                    const stopped = await veterinaryAccessApi.stopMedication(session, id, input, key);
                    setData(await veterinaryAccessApi.context(session));
                    return stopped;
                  }} />
                <HealthSection
                  title="Medicación informada por el tutor"
                  records={data.health.tutorReportedMedications}
                />
                <HealthSection title="Problemas y antecedentes" records={data.health.problems} />
                <HealthSection title="Controles preventivos" records={data.health.preventiveCare} />
              </div>
              <section className="panel vet-access-section">
                <h2>Vacunas ({data.pagination.vaccinations.total})</h2>
                {data.vaccinations.length ? (
                  data.vaccinations.map((vaccination) => (
                    <article className="vet-access-record" key={vaccination.id}>
                      <h3>{vaccination.name}</h3>
                      <HealthFields record={vaccination} />
                    </article>
                  ))
                ) : (
                  <p className="muted">Sin vacunas registradas.</p>
                )}
                {data.pagination.vaccinations.hasMore && (
                  <button
                    className="button button-secondary"
                    disabled={paging}
                    onClick={() => void loadMore("vaccinations")}
                  >
                    Ver más vacunas
                  </button>
                )}
              </section>
              <section className="panel vet-access-section">
                <h2>Historia clínica ({data.pagination.consultations.total})</h2>
                {data.consultations.length ? (
                  data.consultations.map((consultation) => (
                    <article className="vet-access-record" key={consultation.id}>
                      <div className="vet-access-record-heading">
                        <h3>{consultation.reason}</h3>
                        <span className="status status-neutral">
                          {VALUE_LABELS[consultation.status] ?? consultation.status}
                        </span>
                      </div>
                      <p className="muted">
                        {date(consultation.occurredAt)} · {consultation.authorVetName}
                        {consultation.authorVetLicense
                          ? ` · Mat. ${consultation.authorVetLicense}`
                          : ""}
                      </p>
                      <HealthFields
                        record={consultation}
                        fields={[
                          "findings",
                          "anamnesis",
                          "physicalExam",
                          "assessment",
                          "diagnosis",
                          "treatment",
                          "indications",
                          "followUpAt",
                          "voidReason",
                          "organizationName",
                          "sourceType",
                        ]}
                      />
                      {consultation.amendments.map((amendment) => (
                        <aside className="consent-note" key={amendment.id}>
                          <p>
                            Corrección de {amendment.authorVetName} · {date(amendment.createdAt)}
                            <br />
                            {amendment.content}
                          </p>
                        </aside>
                      ))}
                      <ClinicalDocuments
                        organizationId={organizationId}
                        petId={data.pet.id}
                        consultationId={consultation.id}
                        initialCount={consultation.attachmentCount ?? 0}
                        canAttach={consultation.canAttach === true}
                        onCountChange={() =>
                          void veterinaryAccessApi
                            .documents(session)
                            .then(setDocuments)
                            .catch((reason: unknown) => setError(errorText(reason)))
                        }
                        transport={{
                          list: () => veterinaryAccessApi.attachments(session, consultation.id),
                          intent: (file, metadata) =>
                            veterinaryAccessApi.attachmentIntent(
                              session,
                              consultation.id,
                              file,
                              metadata,
                            ),
                          complete: (assetId) =>
                            veterinaryAccessApi.completeAttachment(
                              session,
                              consultation.id,
                              assetId,
                            ),
                        }}
                      />
                    </article>
                  ))
                ) : (
                  <p className="muted">Sin atenciones registradas.</p>
                )}
                {data.pagination.consultations.hasMore && (
                  <button
                    className="button button-secondary"
                    disabled={paging}
                    onClick={() => void loadMore("consultations")}
                  >
                    Ver más atenciones
                  </button>
                )}
              </section>
              <section className="panel vet-access-section">
                <h2>Documentos de salud</h2>
                <p className="muted">
                  Documentación aportada por el tutor y sus profesionales. Acceso privado, sin
                  enlaces públicos permanentes.
                </p>
                {documents.length ? (
                  <ul className="clinical-document-list">
                    {documents.map((document) => (
                      <li key={document.id}>
                        <FileText size={18} />
                        <span>
                          <strong>
                            {document.title || document.originalFilename || "Documento clínico"}
                          </strong>
                          <small>
                            {document.authorName ?? "Tutor"}
                            {document.documentDate ? ` · ${date(document.documentDate)}` : ""}
                          </small>
                        </span>
                        <a
                          className="icon-button"
                          href={document.url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Abrir ${document.title}`}
                        >
                          <ExternalLink size={18} />
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">Sin documentos registrados.</p>
                )}
                <button
                  className="button-link"
                  onClick={() =>
                    void veterinaryAccessApi
                      .documents(session)
                      .then(setDocuments)
                      .catch((reason: unknown) => setError(errorText(reason)))
                  }
                >
                  Actualizar documentos y enlaces privados
                </button>
              </section>
            </>
          )}
        </>
      )}
    </main>
  );
}
